/**
 * @file index.tsx
 * @description 剧本创作/详情页接口文件
 * @author ares
 * @date 2026-04-13
 */

import type {ScriptId} from '@/store/script/interface';
import type {
    ScriptDetailParams,
    StartParseScriptParams,
    ParseScriptResult,
    EditScriptParams,
    ReviewScriptParams,
    SaveScriptParams,
    ScriptDetail,
    ScriptListParams,
    ScriptListData,
    ScriptRenameParams,
    ScriptDeleteParams,
    CloudScriptItem,
    ImportCloudScriptParams,
    ImportCloudScriptResult,
    ScriptConfigResult,
} from '@/services/script/interface';
import request from '@/services/request';

const acsToken = true;

/**
 * 剧本相关 API
 */

// 进入&退出编辑态
export const editScript = async (params: EditScriptParams): Promise<void> => {
    await request.post('proxy-aicomic/comic/script/edit', params);
};

// 获取剧本详情（包括解析状态）
export const fetchScriptDetail = async (params: ScriptDetailParams): Promise<ScriptDetail> => {
    // 生产环境调用真实接口
    return request.get<ScriptDetail>('proxy-aicomic/comic/script/detail', {params});
};

// 启动剧本解析
export const startParseScript = async (
    params: StartParseScriptParams,
): Promise<ParseScriptResult> => {
    return request.post<ParseScriptResult>('proxy-aicomic/comic/script/parse', params, {
        acsToken,
        timeout: 15_000, // 100万字小说解析时间较长，设置超时时间
    });
};

// 剧本塞入预填内容 （适用 解析失败, 进入编辑）
export const preFilledScript = async (scriptId: ScriptId): Promise<string> => {
    return request.post('/proxy-aicomic/comic/script/copy', {scriptId});
};

// 保存剧本
export const saveScript = async (params: SaveScriptParams): Promise<void> => {
    const newParams = {...params, type: Number(params.type)}; // 确保type是数字类型
    await request.post('proxy-aicomic/comic/script/save', newParams);
};

// 编剧提交剧本审核
export const submitScript = async (scriptId: ScriptId): Promise<void> => {
    await request.post('proxy-aicomic/comic/script/submit', {scriptId});
};

// 确认&驳回剧本
export const reviewScript = async (params: ReviewScriptParams): Promise<void> => {
    await request.post('proxy-aicomic/comic/script/confirm', params);
};

// 下载剧本
export const downloadScript = async (scriptId: ScriptId): Promise<string> => {
    return request.get('proxy-aicomic/comic/script/download', {params: {scriptId}});
};

// 获取剧本列表
export const getScriptList = (params: ScriptListParams) =>
    request.get<ScriptListData>('proxy-aicomic/homepage/asset/script/list', {params});

// 剧本重命名
export const renameScript = (data: ScriptRenameParams) =>
    request.post<null>('proxy-aicomic/homepage/asset/script/rename', data, {silent: false});

// 删除剧本
export const deleteScript = (data: ScriptDeleteParams) =>
    request.post<null>('proxy-aicomic/homepage/asset/script/delete', data, {silent: false});
// 获取云端剧本列表
export const getCloudScriptList = () =>
    request.get<CloudScriptItem[]>('proxy-aicomic/comic/script/cloud');

// 云端剧本导入
export const importCloudScript = (data: ImportCloudScriptParams) =>
    request.post<ImportCloudScriptResult>('proxy-aicomic/comic/script/cloud/import', data);

// 剧本模型配置
export const getScriptModelConfig = () =>
    request.get<ScriptConfigResult>('proxy-aicomic/comic/script/model/config');
